import { Button, FormGroup, Intent } from '@blueprintjs/core';
import { IAccessGroup } from 'es/app/business-logic/domain-models/AccessGroup';
import { IFacility } from 'es/app/business-logic/domain-models/Facility';
import { ICompany } from 'es/app/business-logic/domain-models/ICompany';
import Site from 'es/app/business-logic/domain-models/Site';
import { Grid } from 'es/app/components/data-grid/Grid';
import { GridColumns } from 'es/app/components/data-grid/Grid.types';
import { DropDownControl } from 'es/app/components/forms/components';
import { useUpdateFieldValue } from 'es/app/components/forms/FormikForm';
import { useCompaniesAndFacilities } from 'es/app/store/hooks';
import { useAccessGroups } from 'es/app/views/system/people/edit/access-groups/useAccessGroups';
import { SSOUserTemplateFormFields } from 'es/app/views/system/sso-user-templates/SsoUserTemplates.types';
import { useI18n } from 'es/core/hooks/useI18n';
import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import styles from './SSOUserTemplateAccessGroups.module.css';

export function SSOUserTemplateAccessGroups() {
    const { l10n } = useI18n('app/views/system/sso-user-templates');
    const { values } = useFormikContext<SSOUserTemplateFormFields>();
    const setFieldValue = useUpdateFieldValue<SSOUserTemplateFormFields>();
    const {
        companies,
        facilities,
        accessGroups,
        isFetchingAccessGroups,
        selectedCompany,
        selectedFacility,
        selectedAccessGroup,
        isAccessGroupAlreadyIncluded,
        selectCompany,
        selectFacility,
        selectAccessGroup,
    } = useLocalState();
    const columns: GridColumns<SSOUserTemplateFormFields['accessGroups'][number]> = [
        {
            field: 'name',
            headerName: l10n('accessGroups.gridColumns.accessGroup'),
        },
        {
            field: 'owner.key',
            headerName: l10n('accessGroups.gridColumns.authDomain'),
        },
    ];
    return (
        <div className={styles.wrapper}>
            <div className={styles.fieldsWrapper}>
                <FormGroup label={l10n('accessGroups.fields.company.label')} labelFor="access-group-company">
                    <DropDownControl
                        data={companies ?? []}
                        onChange={v => {
                            selectCompany(companies?.find(c => c.id === v) ?? null);
                        }}
                        value={selectedCompany?.id}
                        valueField="id"
                        textField="name"
                        inputId="access-group-company"
                    />
                </FormGroup>
                <FormGroup label={l10n('accessGroups.fields.facility.label')} labelFor="access-group-facility">
                    <DropDownControl
                        data={facilities ?? []}
                        onChange={v => {
                            selectFacility(facilities?.find(f => f.id === v) ?? null);
                        }}
                        value={selectedFacility?.id}
                        valueField="id"
                        textField="name"
                        disabled={!selectedCompany}
                        inputId="access-group-facility"
                    />
                </FormGroup>
                <FormGroup label={l10n('accessGroups.fields.accessGroup.label')} labelFor="access-group-group">
                    <DropDownControl
                        data={accessGroups ?? []}
                        onChange={v => {
                            selectAccessGroup(accessGroups?.find(a => a.id === v) ?? null);
                        }}
                        value={selectedAccessGroup?.id}
                        valueField="id"
                        textField="name"
                        disabled={!selectedCompany || !selectedFacility}
                        inputId="access-group-group"
                        isLoading={isFetchingAccessGroups}
                    />
                </FormGroup>
                <FormGroup>
                    <Button
                        intent={Intent.PRIMARY}
                        type="button"
                        disabled={!selectedAccessGroup || isAccessGroupAlreadyIncluded}
                        title={
                            isAccessGroupAlreadyIncluded
                                ? l10n('accessGroups.fields.accessGroup.validation.alreadyIncluded')
                                : undefined
                        }
                        onClick={() => {
                            if (!selectedFacility || !selectedAccessGroup) {
                                throw new Error(
                                    `Facility and access group must be selected, ${JSON.stringify({ facility: selectedFacility, accessGroup: selectedAccessGroup })}`
                                );
                            }

                            setFieldValue({
                                fieldName: 'accessGroups',
                                fieldValue: [
                                    ...values.accessGroups,
                                    {
                                        ...selectedAccessGroup,
                                        owner: Site.get(
                                            selectedFacility.site.companyAlias,
                                            selectedFacility.site.facilityAlias
                                        ),
                                    },
                                ],
                            });
                        }}
                    >
                        {l10n('actions.add')}
                    </Button>
                </FormGroup>
            </div>
            <div className={styles.gridWrapper}>
                <Grid
                    columns={columns}
                    data={values.accessGroups}
                    rowId={v => `${v.owner.key}_${v.id}`}
                    onDelete={item =>
                        setFieldValue({
                            fieldName: 'accessGroups',
                            fieldValue: values.accessGroups.filter(({ owner: { companyAlias, facilityAlias }, id }) => {
                                return (
                                    companyAlias !== item.owner.companyAlias ||
                                    facilityAlias !== item.owner.facilityAlias ||
                                    id !== item.id
                                );
                            }),
                        })
                    }
                />
            </div>
        </div>
    );
}

function useLocalState() {
    const { values } = useFormikContext<SSOUserTemplateFormFields>();
    const { companies, facilities } = useCompaniesAndFacilities();
    const [company, setCompany] = useState<ICompany | null>(null);
    const [facility, setFacility] = useState<IFacility | null>(null);
    const { accessGroups, isFetchingAccessGroups } = useAccessGroups(facility?.site);
    const [accessGroup, setAccessGroup] = useState<IAccessGroup | null>(null);
    const selectCompany = useCallback((company: ICompany | null) => {
        setCompany(company);
        setFacility(null);
        setAccessGroup(null);
    }, []);
    const selectFacility = useCallback((facility: IFacility | null) => {
        setFacility(facility);
        setAccessGroup(null);
    }, []);
    const selectAccessGroup = useCallback((accessGroup: IAccessGroup | null) => {
        setAccessGroup(accessGroup);
    }, []);
    const isAccessGroupAlreadyIncluded = values.accessGroups.some(
        ag => ag.id === accessGroup?.id && ag.owner.key === facility?.site.key
    );

    return {
        companies,
        facilities: useMemo(() => facilities?.filter(f => f.companyId === company?.id), [company?.id, facilities]),
        accessGroups,
        isFetchingAccessGroups,
        selectedCompany: company,
        selectedFacility: facility,
        selectedAccessGroup: accessGroup,
        isAccessGroupAlreadyIncluded,
        selectCompany,
        selectFacility,
        selectAccessGroup,
    };
}
